
import { Component, ElementRef, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { ScienceService } from '../science.service';
import { Subject, Subscription, filter } from 'rxjs';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { distinctUntilChanged, map, tap, catchError } from 'rxjs/operators';
import { combineLatest, first, of } from 'rxjs';
import { RouterModule, RouterOutlet } from '@angular/router';

@Component({
  selector: 'app-posecoach',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterOutlet, RouterModule],
  templateUrl: './posecoach.component.html',
  styleUrl: './posecoach.component.css'
})
export class PosecoachComponent implements OnInit, OnDestroy {
  @ViewChild('videoElement', { static: true })
  videoElement!: ElementRef<HTMLVideoElement>;

  isWebcamStarted = false;
  isSessionActive = false;
  correctionMessage = '';
  currentExercise = '';
  corrections: any[] = [];
  confidence = 0;
  public isSpeaking = false;
  private pendingCorrection: string | null = null;

  private poseLandmarker: any = null;
  private isInitialized = false;
  private isActiveSubject = new Subject<boolean>();
  private frameInterval: Subscription | null = null;
  private frameCount = 0;
  private sendFrameEvery = 5;
  public currentLandmark: any;
  private subscriptions: Subscription[] = [];
  private lastCoords: number[] | null = null;
  private lastVels: number[] | null = null;

  userLevel = 'intermediate';
  userId = 1;
  availableCameras: MediaDeviceInfo[] = [];
  selectedCameraId = '';
  private lastCorrection: string | null = null;
  private previousCorrection: string | null = null;
  private canRepeatPrevious = false;

  constructor(
    private scienceService: ScienceService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.startWebcam();
    }
  }

  private repeatTimer: any = null;
  private repeatDelayMs = 10000;

  // ================== CORRECTION PRINCIPALE ==================

  async ngOnInit(): Promise<void> {

    // CORRECTION 1: Initialiser currentExercise avec une valeur par défaut
    if (!this.currentExercise) {
      this.currentExercise = 'push-up'; // Seul exercice qui génère exactement 108 features
      console.log('Initialized with default exercise:', this.currentExercise);
    }

    // 1. Chargement des caméras disponibles
    this.availableCameras = await this.scienceService.getAvailableCameras();
    if (this.availableCameras.length > 0) {
      this.selectedCameraId = this.availableCameras[0].deviceId;
    }

    // 2. Souscriptions aux statuts et aux corrections
    this.subscriptions.push(
      this.scienceService.getStreamingStatus()
        .subscribe(status => this.isWebcamStarted = status),

      this.scienceService.getSessionStatus()
        .subscribe(status => this.isSessionActive = status),

      this.scienceService.getCurrentExercise()
        .subscribe(ex => this.currentExercise = ex),

      this.scienceService.getCorrections().pipe(
        filter(corr => Array.isArray(corr) && corr.length > 0)
      ).subscribe(corr => {
        this.corrections = corr;
        const msg = corr[0].message || '';
        if (msg.trim()) {
          this.correctionMessage = msg;
          this.speakCorrection(msg);
        }
      }),

      this.scienceService.getConfidence()
        .subscribe(conf => this.confidence = conf)
    );

    // CORRECTION 2: Améliorer combineLatest avec gestion d'erreur robuste
    this.subscriptions.push(
      combineLatest({
        lm: this.scienceService.getLandmarks().pipe(
          filter(lm => lm != null),
          tap(lm => console.log('Landmarks received:', lm ? 'detected' : 'null'))
        ),
        idx: this.scienceService.getExercisePoints(this.currentExercise).pipe(
          tap(idx => console.log(`Exercise points for "${this.currentExercise}":`, idx)),
          catchError(error => {
            console.error('Error getting exercise points:', error);
            return of([]); // Retourner tableau vide en cas d'erreur
          })
        )
      }).pipe(
        filter(({ idx }) => {
          const hasIndices = idx.length > 0;
          if (!hasIndices) {
            console.error('No exercise points found for:', this.currentExercise);
            console.log('Available exercises should be checked in mappings');
          }
          return hasIndices;
        }),
        // CORRECTION 3: Retirer first() pour permettre les émissions continues
        // first() // Commenté car cela empêche les mises à jour continues
        tap(({ lm, idx }) => {
          console.log('CombineLatest emitted - Landmarks:', !!lm, 'Indices:', idx.length);
        })
      ).subscribe({
        next: ({ lm, idx }) => {
          try {
            console.log('Processing frame with exercise:', this.currentExercise);

            // Extraction des features avec logs détaillés
            const { features, lastCoords, lastVels } =
              this.scienceService.extractFeatures(
                lm, this.currentExercise, idx, this.lastCoords, this.lastVels
              );

            console.log('Features extracted - Length:', features.length);
            console.log('First 5 features:', features.slice(0, 5));

            // CORRECTION 4: Vérification stricte de la dimension
            if (features.length !== 108) {
              console.error(`DIMENSION ERROR: Expected 108, got ${features.length}`);
              console.error('Exercise:', this.currentExercise, 'Indices count:', idx.length);
              return; // Arrêter le traitement si dimension incorrecte
            }

            this.lastCoords = lastCoords;
            this.lastVels = lastVels;

            // Envoi des données au backend
            this.scienceService.sendPoseData(features, this.userLevel, this.userId)
              .subscribe({
                next: resp => {
                  console.log('API Response received:', resp);
                  this.currentExercise = resp.exercise;
                  this.corrections = resp.corrections;
                  this.confidence = resp.confidence;
                  this.correctionMessage = resp.corrections?.[0]?.message || '';
                },
                error: err => {
                  console.error('API Error:', err);
                  this.correctionMessage = 'Erreur serveur, réessayez.';
                }
              });

          } catch (error) {
            console.error('Error in ngOnInit combineLatest:', error);
          }
        },
        error: error => {
          console.error('CombineLatest error:', error);
        }
      })
    );
  }

  // ================== MÉTHODES DE DEBUG ==================

  /**
   * Méthode pour diagnostiquer l'état actuel du système
   */
  debugCurrentState(): void {
    console.log('=== DEBUG STATE ===');
    console.log('Current exercise:', this.currentExercise);
    console.log('Webcam started:', this.isWebcamStarted);
    console.log('Session active:', this.isSessionActive);
    console.log('Video element ready:', this.videoElement?.nativeElement?.readyState);
    console.log('Last coords length:', this.lastCoords?.length || 0);
    console.log('Last vels length:', this.lastVels?.length || 0);

    // Tester la récupération des exercise points
    this.scienceService.getExercisePoints(this.currentExercise).subscribe({
      next: points => console.log('Exercise points test:', points),
      error: err => console.error('Exercise points error:', err)
    });
  }

  /**
   * Méthode pour tester l'extraction de features manuellement
   */
  testFeatureExtraction(): void {
    console.log('Testing feature extraction...');

    // Simuler des landmarks basiques pour test
    const mockLandmarks = Array.from({ length: 33 }, (_, i) => ({
      x: Math.random(),
      y: Math.random(),
      z: Math.random()
    }));

    this.scienceService.getExercisePoints(this.currentExercise).subscribe(idx => {
      if (idx.length > 0) {
        const { features } = this.scienceService.extractFeatures(
          mockLandmarks, this.currentExercise, idx, null, null
        );
        console.log('Test extraction result - Length:', features.length);
        console.log('Test features preview:', features.slice(0, 10));
      } else {
        console.error('No exercise points for test');
      }
    });
  }

  // ================== MÉTHODES EXISTANTES (avec améliorations) ==================

  startSession(): void {
    if (!this.isWebcamStarted) {
      alert('Veuillez d\'abord démarrer la webcam');
      return;
    }

    // CORRECTION 5: Vérifier l'exercice avant de démarrer
    if (!this.currentExercise) {
      console.warn('No current exercise set, using default');
      this.currentExercise = 'push-up';
    }

    console.log('Starting session with exercise:', this.currentExercise);

    this.scienceService.startSession(
      this.videoElement.nativeElement,
      this.userLevel,
      this.currentExercise,
      this.userId
    );
  }

  setUserLevel(level: string): void {
    this.userLevel = level;
    console.log('User level set to:', level);
  }

  // Méthode pour changer d'exercice manuellement (utile pour debug)
  setCurrentExercise(exercise: string): void {
    console.log('Changing exercise from', this.currentExercise, 'to', exercise);
    this.currentExercise = exercise;

    // Tester immédiatement les exercise points
    this.scienceService.getExercisePoints(exercise).subscribe({
      next: points => console.log('New exercise points:', points),
      error: err => console.error('Error getting points for new exercise:', err)
    });
  }

  // ================== MÉTHODES EXISTANTES INCHANGÉES ==================

  private setupRepeatTimer(message: string | undefined): void {
    if (this.repeatTimer) {
      clearTimeout(this.repeatTimer);
      this.repeatTimer = null;
    }

    if (!message || message.trim() === '') return;

    this.repeatTimer = setTimeout(() => {
      if (this.corrections.length > 0 && this.corrections[0]?.message === message) {
        console.log('🔁 Répétition automatique de :', message);
        this.speakCorrection(message);
      }
    }, this.repeatDelayMs);
  }

  private speakCorrection(message: string): void {
    if (!message.trim()) return;
    if (this.isSpeaking) {
      this.pendingCorrection = message;
      return;
    }

    this.pendingCorrection = null;
    this.readText(message);
  }

  private readText(text: string): void {
    speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = 'fr-FR';
    this.isSpeaking = true;

    utter.onend = () => {
      this.isSpeaking = false;
      if (this.pendingCorrection && this.pendingCorrection !== text) {
        const next = this.pendingCorrection;
        this.pendingCorrection = null;
        this.readText(next);
      }
    };

    utter.onerror = () => {
      this.isSpeaking = false;
      this.pendingCorrection = null;
    };

    speechSynthesis.speak(utter);
  }

  async startWebcam(): Promise<void> {
    try {
      await this.scienceService.startWebcam(this.videoElement.nativeElement);
    } catch (error) {
      console.error('Impossible de démarrer la webcam :', error);
    }
  }

  stopWebcam(): void {
    this.scienceService.stopWebcam();
  }

  async selectCamera(): Promise<void> {
    if (this.selectedCameraId) {
      await this.scienceService.selectCamera(this.selectedCameraId);
    }
  }

  getConfidenceColor(): string {
    if (this.confidence > 0.8) return 'green';
    if (this.confidence > 0.6) return 'orange';
    return 'red';
  }

  getCorrectionSeverityClass(correction: any): string {
    return correction.severity || 'info';
  }


  stopSession(): void {
    this.isActiveSubject.next(false);
    if (this.frameInterval) {
      this.frameInterval.unsubscribe();
      this.frameInterval = null;
    }
    this.frameCount = 0;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.scienceService.stopSession();
    this.scienceService.stopWebcam();
    if (this.poseLandmarker) {
      this.poseLandmarker.close();
      this.poseLandmarker = null;
    }
  }
}
